import React from "react";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {daysFromToday, faDate, faDigits, faShortDate} from "./meetings-labels";
import {LocationText, StatusPill} from "./meetings-ui";
import {openMyMeeting} from "./meeting-sidebar";
import {useMyMeetings} from "./my-meetings-store";
import "./my-meetings.css";

// A meeting in a person's task list and board (boss's task, 2026-10-27; docs/tasks/30-meeting-cards.md): drawn
// differently from a task — a blue tint, a blue edge, a calendar icon and «جلسه» — and a click opens its sidebar.

const RECENT = 14;

// a meeting as a row of the task list (the list's columns branch on `__meeting`)
export const meetingRow = (meeting) => ({...meeting, __meeting: true, id: `meeting:${meeting.key}`});

// into the list's time groups: late (still «در پیش» but past), today, this week, later, and finished ones of the last days
export function bucketMeetings(meetings, today) {
    const buckets = {overdue: [], today: [], week: [], future: [], done: []};
    if (!today) return buckets;
    for (const meeting of meetings || []) {
        const days = daysFromToday(meeting.date, today);
        if (meeting.status !== "scheduled") {
            if (days >= -RECENT) buckets.done.push(meetingRow(meeting));
        } else if (days < 0) buckets.overdue.push(meetingRow(meeting));
        else if (days === 0) buckets.today.push(meetingRow(meeting));
        else if (days <= 7) buckets.week.push(meetingRow(meeting));
        else buckets.future.push(meetingRow(meeting));
    }
    return buckets;
}

export const timeText = (meeting) => `${faDigits(meeting.start)} تا ${faDigits(meeting.end)}`;

// cells of the list for a meeting row
export const MeetingCells = {
    check: () => <span className="mc-icon" title="جلسه"><Icon icon="mi mi:event"/></span>,
    title: (row) => (
        <div className="flex flex-column gap-1">
            <span className="flex align-items-center gap-2 flex-wrap">
                <span className="mc-pill"><Icon icon="mi mi:groups" className="text-xs"/>جلسه</span>
                <b className={`text-sm ${row.status === "scheduled" ? "" : "text-500"}`}>{row.title}</b>
                {row.status !== "scheduled" && <StatusPill kind="meeting" status={row.status}/>}
                {row.mine?.owner && <span className="mc-mine">مسئول جلسه‌اید</span>}
            </span>
            <span className="mc-sub">
                <span>{timeText(row)}</span>
                <LocationText text={row.location}/>
                {row.statusReason && <span>{`دلیل: ${row.statusReason}`}</span>}
            </span>
        </div>
    ),
    owner: (row) => (row.owner?.member ? <span title="مسئول جلسه"><Avatar memberData={row.owner.member}/></span> : null),
    date: (row) => <span className="text-xs">{faDate(row.date)}</span>,
    project: (row) => (row.project ? <span className="border-round px-1 border-1 border-200 surface-100 text-2xs">{row.project.title}</span> : null),
};

// the row's own class in the list
export const meetingRowClass = (row) => (row?.__meeting ? `mc-row ${row.status === "scheduled" ? "" : "is-closed"}` : "");

// a strip of the next meetings above the task board
export function MyMeetingsStrip() {
    const {meetings, today} = useMyMeetings();
    const upcoming = (meetings || []).filter((meeting) => meeting.status === "scheduled" && daysFromToday(meeting.date, today) >= 0).slice(0, 12);
    if (!upcoming.length) return null;
    return (
        <div className="mc-strip" aria-label="جلسه‌های پیش‌رو">
            <span className="mc-strip-title"><Icon icon="mi mi:groups" className="text-sm"/>جلسه‌های من</span>
            <div className="mc-strip-list">
                {upcoming.map((meeting) => (
                    <button key={meeting.key} type="button" className="mc-card" onClick={() => openMyMeeting(meeting.key)}>
                        <span className="mc-card-date"><b>{faShortDate(meeting.date)}</b><small>{faDigits(meeting.start)}</small></span>
                        <span className="flex-1 min-w-0 text-right">
                            <b className="text-xs block text-overflow-ellipsis white-space-nowrap overflow-hidden">{meeting.title}</b>
                            <span className="mc-sub">{meeting.project?.title || timeText(meeting)}</span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
